import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import type { Field } from "@project/admin/components/resource-table";
import { festivalService } from "@project/services/admin/festival.service";
import { jainPanchangService } from "@project/services/admin/jain-panchang.service";
import {
  JAIN_MONTH_OPTIONS,
  JAIN_TITHI_OPTIONS,
  PAKSHA_OPTIONS,
  PANCHANG_TYPE_OPTIONS,
  type JainPanchang,
  type PanchangType,
} from "@project/types/jain-panchang";

const fields: Field[] = [
  {
    name: "english_date",
    label: "English Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  {
    name: "type",
    label: "Type",
    type: "select",
    required: true,
    showInTable: true,
    placeholder: "Select type...",
    options: [...PANCHANG_TYPE_OPTIONS],
  },
  {
    name: "jain_month",
    label: "Jain Month",
    type: "select",
    required: true,
    showInTable: true,
    options: [...JAIN_MONTH_OPTIONS],
  },
  {
    name: "paksha",
    label: "Paksha",
    type: "select",
    required: true,
    showInTable: true,
    options: [...PAKSHA_OPTIONS],
  },
  {
    name: "jain_tithi",
    label: "Jain Tithi",
    type: "select",
    required: true,
    showInTable: true,
    options: [...JAIN_TITHI_OPTIONS],
  },
  {
    name: "festival",
    label: "Festival",
    showInTable: true,
    lookup: { table: "festivals", labelField: "name" },
    help: "Optional — link to a festival from the Festivals master",
  },
  {
    name: "religious_occasion",
    label: "Religious Occasion",
    showInTable: true,
    placeholder: "New month beginning",
  },
  {
    name: "short_description",
    label: "Short Description",
    type: "textarea",
    placeholder: "First day of Kartak Sud",
  },
  {
    name: "significance",
    label: "Significance",
    type: "textarea",
    placeholder: "Auspicious day for spiritual activities",
  },
  {
    name: "recommended_activities",
    label: "Recommended Activities",
    type: "textarea",
    placeholder: "Fasting, temple visit, meditation",
  },
  {
    name: "donation_message",
    label: "Donation Message",
    type: "textarea",
    placeholder: "Support elder care on this sacred day",
  },
  {
    name: "display_order",
    label: "Display Order",
    type: "number",
    showInTable: true,
    placeholder: "1",
  },
  {
    name: "show_on_website",
    label: "Show on Website",
    type: "boolean",
    showInTable: true,
  },
  {
    name: "is_important",
    label: "Important",
    type: "boolean",
    showInTable: true,
  },
  {
    name: "highlight_in_calendar",
    label: "Highlight in Calendar",
    type: "boolean",
    showInTable: true,
  },
  {
    name: "show_in_upcoming",
    label: "Show in Upcoming",
    type: "boolean",
    showInTable: true,
  },
  {
    name: "status",
    label: "Active",
    type: "boolean",
    showInTable: true,
  },
];

function toPanchangType(value: unknown): PanchangType {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (raw === "hindu" || raw === "jain") {
    return raw;
  }
  throw new Error("Select a valid type.");
}

function toJainPanchangPayload(payload: Record<string, unknown>) {
  return {
    english_date: String(payload.english_date),
    type: toPanchangType(payload.type),
    jain_month: String(payload.jain_month),
    paksha: String(payload.paksha),
    jain_tithi: String(payload.jain_tithi),
    festival:
      payload.festival !== undefined && payload.festival !== ""
        ? Number(payload.festival)
        : undefined,
    religious_occasion: payload.religious_occasion
      ? String(payload.religious_occasion)
      : undefined,
    show_on_website: Boolean(payload.show_on_website),
    is_important: Boolean(payload.is_important),
    highlight_in_calendar: Boolean(payload.highlight_in_calendar),
    show_in_upcoming: Boolean(payload.show_in_upcoming),
    display_order: Number(payload.display_order ?? 1),
    status: Boolean(payload.status),
    short_description: payload.short_description
      ? String(payload.short_description)
      : undefined,
    significance: payload.significance
      ? String(payload.significance)
      : undefined,
    recommended_activities: payload.recommended_activities
      ? String(payload.recommended_activities)
      : undefined,
    donation_message: payload.donation_message
      ? String(payload.donation_message)
      : undefined,
  };
}

export default function Page() {
  return (
    <AdminShell
      title="Jain Panchang"
      subtitle="Manage daily Jain calendar entries, tithis, and festival links"
    >
      <ApiResourceTable<JainPanchang>
        title="Panchang Entry"
        labelField="english_date"
        fields={fields}
        searchFields={[
          "english_date",
          "jain_month",
          "jain_tithi",
          "religious_occasion",
          "short_description",
        ]}
        defaultValues={{
          type: "jain",
          show_on_website: true,
          is_important: false,
          highlight_in_calendar: false,
          show_in_upcoming: true,
          display_order: 1,
          status: true,
        }}
        lookupSearchLoaders={{
          festival: async query => {
            const result = await festivalService.listFestivalsPage({
              page: 1,
              page_size: 50,
              search: query || undefined,
            });
            return result.data.map(f => ({
              value: String(f.id),
              label: f.name,
            }));
          },
        }}
        list={query => jainPanchangService.listJainPanchangPage(query)}
        create={payload =>
          jainPanchangService.createJainPanchang(toJainPanchangPayload(payload))
        }
        update={(id, payload) =>
          jainPanchangService.updateJainPanchang(
            id,
            toJainPanchangPayload(payload)
          )
        }
        remove={id => jainPanchangService.deleteJainPanchang(id)}
      />
    </AdminShell>
  );
}
